/**
 * The decisions a split pane makes, apart from the DOM it makes them about.
 *
 * `ACCEPTANCE.md`'s Obsidian row asks for split panes. Most of that feature is
 * host plumbing — a container, a second generated lens, a repaint — and is
 * asserted in a browser by `e2e/split-panes.mjs`, because "these two panes do
 * not overlap" is a fact about computed layout that no unit test can see.
 *
 * What lives here is the part that is a decision rather than a rendering: what
 * a click means, and what a pane is called. Both were wrong in a first version
 * in ways a browser found late and a test finds immediately.
 */

/**
 * Does this click mean "open to the side" rather than "open here"?
 *
 * The modifier Obsidian uses, and the one every browser already trains people
 * on for "somewhere else": Ctrl, or Cmd on a Mac.
 *
 * Middle-click belongs to that habit and is deliberately NOT here: a middle
 * button produces no `click` event at all — it produces `auxclick` — so
 * `button === 1` in a click handler is a condition that can never be true. A
 * first version had it, and a claim the code cannot make is worse than a
 * missing one.
 */
export const wantsAside = (e: { ctrlKey: boolean; metaKey: boolean }): boolean =>
  e.ctrlKey || e.metaKey;

/** The roles a pane header will accept as a name, in order of preference. */
const NAME_ROLES = ['title', 'name', 'label'] as const;

/**
 * What to call the object in a pane's header.
 *
 * Read from the PROJECTION rather than from the pane's markup. The markup is
 * the renderer's, so reading a title back out of it would make the header
 * depend on which presentation the generator happened to choose that pass —
 * and a header that changes when the layout does is a header describing the
 * layout instead of the object.
 *
 * A blank or whitespace-only value is not a name. Falling through to the
 * placeholder is better than a header that renders as an empty box the user
 * cannot tell from a bug.
 */
export function titleOf(row: Record<string, unknown> | undefined): string {
  for (const role of NAME_ROLES) {
    const v = row?.[role];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return 'Object';
}
